import { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { getIn, useField, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import type {
  Hospital,
  HospitalFormBuilderField,
  HospitalSchema,
  MhFormContext,
} from "@thalamos/aspire-common";

import { config } from "../../config.js";
import { ManagedLocationsContext } from "../../Contexts.js";
import type { FieldProps } from "../../pages/FieldProps.js";
import { AddressField } from "../design-system/AddressField/AddressField.js";
import { FormLabel } from "../design-system/FormLabel.js";
import { HelperText } from "../design-system/HelperText/HelperText.js";
import { Typeahead } from "../design-system/Typeahead/Typeahead.js";
import type { SelectedTrust } from "./TrustPicker.js";
import { TrustPicker } from "./TrustPicker.js";

export type HospitalPickerProps<Data extends { [k: string]: any }> = {
  field: HospitalFormBuilderField<Data>;
  fieldProps: FieldProps<Data>;
  trustAutoSelectedMessageOverride?: string;
};

type HospitalAndCombined = Hospital & { combined: string };

export function HospitalPicker<Data extends { [k: string]: any }>({
  field,
  fieldProps,
  trustAutoSelectedMessageOverride,
}: HospitalPickerProps<Data>) {
  const { submitCount } = useFormikContext();

  const selected = fieldProps.values[field.field] as HospitalSchema | undefined;

  const { t } = useTranslation();

  const managedLocationsContext = useContext(ManagedLocationsContext);

  const selectedHospital =
    selected?.hospitalId &&
    managedLocationsContext.managedLocations.find(
      (h) => h.id === selected.hospitalId,
    );

  const notListedTrust = managedLocationsContext.trusts.find(
    (t) => t.notListed,
  );
  const trustUnknown = selected?.trustId === notListedTrust?.id;

  const hospitals: HospitalAndCombined[] =
    managedLocationsContext.managedLocations
      .filter((location) => location.type === "hospital")
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((location) => {
        const hospitalAddress = location.address;

        const matchedTrust = managedLocationsContext.trusts.find(
          (t) => t.id === location.trustId,
        );

        return {
          hospitalId: location.id,
          trustId: location.trustId ?? undefined,
          trustName: matchedTrust?.name,
          trustOdsCode: matchedTrust?.odsCode ?? undefined,
          name: location.name,
          address: {
            address: hospitalAddress,
            postalCode: location.postalCode,
          },
          combined: `${location.name}, ${hospitalAddress}`,
        };
      });

  const [address, setAddress] = useState({
    address: selected?.address || "",
    postalCode: selected?.postalCode || "",
    isConfirmed: selected?.isConfirmed || false,
  });

  // Update validation to skip if trustUnknown is true
  const [_trustField, trustFieldMeta, _helpers] = useField({
    name: `${fieldProps.formPrefixKey ? `${fieldProps.formPrefixKey}.` : ""}${field.field}.trustId`,
    validate: (value) => {
      if (trustUnknown) return undefined;
      if (getIn(fieldProps.touched, `${field.field}`)) {
        return !value ? "Please select a trust" : undefined;
      }
    },
  });

  useEffect(() => {
    if (submitCount > 0 && !selected?.trustId && !trustUnknown) {
      fieldProps.setFieldTouched(`${field.field}.trustId`, true, true);
    }
  }, [submitCount, selected?.trustId, trustUnknown]);

  // Update form state of address from local component state of address
  useEffect(() => {
    if (
      address.address !== (selected?.address || "") ||
      address.postalCode !== (selected?.postalCode || "") ||
      address.isConfirmed !== (selected?.isConfirmed || false)
    ) {
      fieldProps.setFieldTouched(`${field.field}.address`, true, false);
      fieldProps.setFieldTouched(`${field.field}.postalCode`, true, false);

      const newValues = {
        ...fieldProps.values,
        [field.field]: { ...(selected || {}), ...address },
      };
      fieldProps.setValues(newValues);
    }
  }, [JSON.stringify(address)]);

  // Set default value if there is no value and the field definition has a defaultFn
  useEffect(() => {
    if (
      !selected?.name &&
      !selected?.address &&
      !selected?.postalCode &&
      !selected?.isConfirmed &&
      field.defaultFn
    ) {
      let defaultValue = field.defaultFn(
        fieldProps.values,
        fieldProps.context as MhFormContext,
      );
      if (defaultValue) {
        setAddress({
          address: defaultValue?.address ?? "",
          postalCode: defaultValue?.postalCode ?? "",
          isConfirmed: false,
        });
        fieldProps.setValues({
          ...fieldProps.values,
          [field.field]: { ...defaultValue, isConfirmed: false },
        });
      }
    } else if (selected === undefined) {
      setAddress({
        address: "",
        postalCode: "",
        isConfirmed: false,
      });
      fieldProps.setValues({
        ...fieldProps.values,
        [field.field]: { name: "", isConfirmed: false },
      });
    }
  }, [JSON.stringify(selected)]);

  const nameErrors =
    getIn(fieldProps.touched, `${field.field}.name`) &&
    getIn(fieldProps.errors, `${field.field}.name`);

  const addressErrors =
    getIn(fieldProps.touched, `${field.field}.address`) &&
    getIn(fieldProps.errors, `${field.field}.address`);

  const postalCodeErrors =
    getIn(fieldProps.touched, `${field.field}.postalCode`) &&
    getIn(fieldProps.errors, `${field.field}.postalCode`);

  const confirmErrors =
    getIn(fieldProps.touched, `${field.field}.isConfirmed`) &&
    getIn(fieldProps.errors, `${field.field}.isConfirmed`);

  return (
    <>
      {field.label && (
        <FormLabel label={field.label} error={false} tooltip={field.tooltip} />
      )}
      <Typeahead
        testId={`${field.field}.name`}
        label="Hospital Name"
        enableFuse={true}
        placeholder={t("forms.hospitalPicker.typeaheadPlaceholder")}
        errorMessage={
          nameErrors
            ? getIn(fieldProps.errors, `${field.field}.name`)
            : undefined
        }
        showHelperText={!!nameErrors}
        name={`${field.field}.name`}
        inputValue={selected?.name || ""}
        optionsKey={"combined"}
        onInputChange={(value: string | null) => {
          fieldProps.setFieldTouched(`${field.field}.name`, true, false);

          if (value) {
            const hospital = hospitals.find((h) => h.combined === value);
            const matchedTrust = managedLocationsContext.trusts.find(
              (t) => t.id === hospital?.trustId,
            );

            if (hospital) {
              setAddress({
                address: hospital.address.address,
                postalCode: hospital.address.postalCode,
                isConfirmed: true,
              });
            } else {
              setAddress((prevState) => {
                return {
                  ...prevState,
                  isConfirmed: false,
                };
              });
            }

            fieldProps.setValues({
              ...fieldProps.values,
              [field.field]: {
                ...selected,
                ...(hospital
                  ? {
                      name: hospital.name,
                      hospitalId: hospital.hospitalId,
                      trustId: hospital.trustId,
                      trustName: matchedTrust?.name,
                      trustOdsCode: matchedTrust?.odsCode ?? undefined,
                      isConfirmed: true,
                    }
                  : {
                      name: value,
                      hospitalId: undefined,
                      trustId: undefined,
                      trustName: undefined,
                      trustOdsCode: undefined,
                    }),
              },
            });
          } else {
            setAddress({
              address: "",
              postalCode: "",
              isConfirmed: false,
            });
            fieldProps.setValues({
              ...fieldProps.values,
              [field.field]: { name: "" },
            });
          }
        }}
        options={hospitals}
        getOptionLabel={(hospital: HospitalAndCombined | string) => {
          if (typeof hospital === "string") {
            return hospital;
          } else {
            return hospital.combined;
          }
        }}
      />

      <Box sx={{ mb: 3 }}>
        {!field.hideAddress && (
          <>
            <AddressField
              testId={`${field.field}.address`}
              placeholder={{
                address: t("forms.hospitalPicker.addressPlaceholder"),
                postalCode: t("forms.hospitalPicker.postalCodePlaceholder"),
              }}
              addressLabel={t("forms.hospitalPicker.addressLabel")}
              postalLabel={t("forms.hospitalPicker.postalLabel")}
              subtextIcon="error"
              showHelperText={
                addressErrors || postalCodeErrors || confirmErrors
              }
              errors={{
                address: addressErrors,
                postalCode: postalCodeErrors,
                isConfirmed: confirmErrors,
              }}
              googleMapsApiKey={config.googleMapsApiKey}
              addressCheckBoxLabel={t(
                "forms.hospitalPicker.addressCheckBoxLabel",
              )}
              name={field.field}
              value={address}
              onChange={setAddress}
            />
            {field.afterLabel && <HelperText subtext={field.afterLabel} />}
          </>
        )}

        {!selectedHospital || !selectedHospital.trustId ? (
          <>
            <Box sx={{ mb: "1rem", mt: "1rem" }}>
              <TrustPicker
                value={
                  selected?.trustId || selected?.trustName
                    ? {
                        id: selected.trustId,
                        name: selected.trustName ?? "",
                        odsCode: selected.trustOdsCode,
                        isNotListed: trustUnknown,
                      }
                    : null
                }
                onChange={(trust: SelectedTrust) => {
                  fieldProps.setFieldTouched(
                    `${field.field}.name`,
                    true,
                    false,
                  );

                  if (trust) {
                    fieldProps.setValues({
                      ...fieldProps.values,
                      [field.field]: {
                        ...selected,
                        trustId: trust.id,
                        trustName: trust.name,
                        trustOdsCode: trust.odsCode ?? "",
                      },
                    });
                  } else {
                    fieldProps.setValues({
                      ...fieldProps.values,
                      [field.field]: {
                        ...selected,
                        trustId: undefined,
                        trustName: "",
                        trustOdsCode: "",
                      },
                    });
                    fieldProps.setFieldTouched(
                      `${field.field}.trustId`,
                      true,
                      true,
                    );
                  }
                }}
                allowNotListed={true}
                errorMessage={trustFieldMeta.error}
              />
            </Box>
            <HelperText
              noteMessage={
                trustAutoSelectedMessageOverride ??
                t("forms.hospitalPicker.trustAutoSelected")
              }
            />
          </>
        ) : (
          <Box>
            <Box sx={{ mb: "1rem", mt: "1rem", fontWeight: "bold" }}>
              Trust: {selected?.trustName}
            </Box>

            <HelperText
              noteMessage={
                trustAutoSelectedMessageOverride ??
                t("forms.hospitalPicker.trustAutoSelected")
              }
            />
          </Box>
        )}
      </Box>
    </>
  );
}
